import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { PdpMockEditor } from '../../components/PdpMockEditor';

// Modal listing every student in a batch with the PdpMockEditor per row.
// Consumes GET /api/training/reports/trainer-detail?batch_id=... to get students
// with their remarks (pdp / mock_interview / mock_interview_link).
export const BatchPdpMockModal = ({ open, onOpenChange, batch }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!open) {
      // Prevent late renders during close from touching stale batch props.
      setRows(null);
      return;
    }
    if (!batch?.batch_id) return;
    setRows(null);
    api.get(`/training/reports/trainer-detail/${batch.batch_id}/students`)
      .then((r) => {
        // endpoint returns { students: [...] } with pdp/mock/link fields
        setRows(r.data.students || []);
      })
      .catch(() => setRows([]));
  }, [open, batch?.batch_id]);

  const total = rows?.length || 0;
  const missingPdp = (rows || []).filter((s) => !s.pdp).length;
  const missingMock = (rows || []).filter((s) => !s.mock_interview).length;
  const missingLink = (rows || []).filter((s) => !s.mock_interview_link).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="batch-pdp-mock-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-slate-800">
            Fill PDP &amp; Mock Interview · #{batch?.batch_number} · {batch?.batch_name}
          </DialogTitle>
          <p className="text-[10px] text-slate-500">
            {total} student{total === 1 ? '' : 's'} · {missingPdp} missing PDP · {missingMock} missing mock · {missingLink} missing link
          </p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="batch-pdp-mock-body">
          {!batch || rows === null ? (
            <div className="flex items-center justify-center py-14"><Loader2 className="w-4 h-4 animate-spin text-violet-600" /></div>
          ) : total === 0 ? (
            <p className="text-center text-xs text-slate-400 py-10">No enrolled students in this batch.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              <div className="grid grid-cols-[220px_1fr] gap-3 text-[10px] uppercase tracking-wide text-slate-400 font-semibold py-2">
                <span>Student</span><span>PDP · Mock · Video/Transcript</span>
              </div>
              {rows.map((s) => (
                <div key={s.student_id} className="grid grid-cols-[220px_1fr] gap-3 py-2 items-center" data-testid={`batch-pdp-mock-row-${s.student_id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-800 truncate" title={s.name}>{s.name || '(unknown)'}</p>
                    <p className="text-[10px] text-slate-400 truncate">{s.enrollment_no || ''}</p>
                  </div>
                  <PdpMockEditor
                    batchId={batch.batch_id}
                    studentId={s.student_id}
                    initial={{ pdp: s.pdp, mock_interview: s.mock_interview, mock_interview_link: s.mock_interview_link }}
                    endpoint="admin"
                    compact
                    onSaved={(next) => {
                      setRows((prev) => prev.map((r) => r.student_id === s.student_id ? { ...r, ...next } : r));
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BatchPdpMockModal;
